import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  TrendingUp,
  Users,
  Star,
  CheckCircle2,
  Package,
  ChevronRight,
  BarChart3,
  Loader2,
} from "lucide-react";
import { motion } from "motion/react";
import { leadsService } from "../services/leadsService";
import { companiesService } from "../services/companiesService";
import { catalogService } from "../services/catalogService";
import { useAppStore } from "../store/useAppStore";
import type { Company, Lead, Product } from "../types";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { LeadCard } from "../components/LeadCard";
import { useTranslation } from "react-i18next";

const DEMO_COMPANY_ID = "te-1";

export function ExhibitorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId } = useAppStore();
  const eventId = selectedEventId ?? "";
  const companyId = DEMO_COMPANY_ID;

  const [company, setCompany] = useState<Company | undefined>();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [comp, leadList, productList] = await Promise.all([
          companiesService.getById(companyId, eventId, "visitor"),
          leadsService
            .listByCompany(eventId, companyId, "visitor")
            .then((r) => r.leads)
            .catch(() => [] as Lead[]),
          catalogService.listProducts(eventId, companyId).catch(() => [] as Product[]),
        ]);
        if (!cancelled) {
          setCompany(comp);
          setLeads(leadList);
          setProducts(productList);
        }
      } catch {
        if (!cancelled) {
          setCompany(undefined);
          setLeads([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  const stats = useMemo(() => {
    const total = leads.length;
    const newLeads = leads.filter((l) => l.status === "new").length;
    const qualified = leads.filter((l) => l.status === "qualified").length;
    const closed = leads.filter((l) => l.status === "closed").length;
    const avgRating =
      leads.filter((l) => l.rating).reduce((sum, l) => sum + (l.rating || 0), 0) /
      (leads.filter((l) => l.rating).length || 1);

    return { total, newLeads, qualified, closed, avgRating };
  }, [leads]);

  const recentLeads = useMemo(() => {
    return [...leads]
      .sort((a, b) => new Date(b.scannedAt).getTime() - new Date(a.scannedAt).getTime())
      .slice(0, 5);
  }, [leads]);

  if (loading) {
    return (
      <PageShell>
        <div className="flex justify-center pt-20 text-gray-400">
          <Loader2 size={28} className="animate-spin" />
        </div>
      </PageShell>
    );
  }

  if (!company) return null;

  return (
    <PageShell noPadding>
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-4 pt-8 pb-6">
        <h1 className="text-[24px] font-semibold text-white mb-1">{t("exhibitor.dashboard_title")}</h1>
        <p className="text-[14px] text-indigo-100">{company.name}</p>
      </div>

      <div className="px-4 -mt-4 pb-24 space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <TrendingUp size={20} className="text-blue-600" />
                </div>
              </div>
              <div className="text-[28px] font-semibold text-gray-900">{stats.total}</div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.stats_total_leads")}</div>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Users size={20} className="text-purple-600" />
                </div>
              </div>
              <div className="text-[28px] font-semibold text-gray-900">{stats.newLeads}</div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.stats_new")}</div>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Star size={20} className="text-amber-600" />
                </div>
              </div>
              <div className="text-[28px] font-semibold text-gray-900">
                {stats.avgRating.toFixed(1)}
              </div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.stats_avg_rating")}</div>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 size={20} className="text-emerald-600" />
                </div>
              </div>
              <div className="text-[28px] font-semibold text-gray-900">{stats.closed}</div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.stats_closed")}</div>
            </GlassCard>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/exhibitor/leads")}
              className="p-4 rounded-2xl bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mb-3">
                <BarChart3 size={20} className="text-indigo-600" />
              </div>
              <div className="text-[14px] font-medium text-gray-900 mb-0.5">{t("exhibitor.all_leads")}</div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.manage")}</div>
            </button>

            <button
              onClick={() => navigate(`/companies/${company.id}/products`)}
              className="p-4 rounded-2xl bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-3">
                <Package size={20} className="text-amber-600" />
              </div>
              <div className="text-[14px] font-medium text-gray-900 mb-0.5">{t("exhibitor.products")}</div>
              <div className="text-[12px] text-gray-500">{t("exhibitor.products_count", { n: products.length })}</div>
            </button>
          </div>
        </motion.div>

        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[16px] font-medium text-gray-900">{t("exhibitor.recent_leads")}</h2>
            <button
              onClick={() => navigate("/exhibitor/leads")}
              className="text-[13px] text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              {t("home.see_all")}
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="space-y-2">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead) => <LeadCard key={lead.id} lead={lead} />)
            ) : (
              <GlassCard variant="subtle" padding="lg">
                <div className="text-center py-4">
                  <p className="text-[14px] text-gray-500">{t("leads.empty_title")}</p>
                  <p className="text-[12px] text-gray-400 mt-1">{t("leads.empty_desc")}</p>
                </div>
              </GlassCard>
            )}
          </div>
        </motion.div>
      </div>
    </PageShell>
  );
}
